import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import LoadingScreen from '../ReusableBlocks/LoadingScreen';
import { fetchFeatureFlags } from './featureFlagApi';
import { isFeatureEnabled as checkFeatureEnabled } from './featureFlagAccess';

const FeatureFlagsContext = createContext({
  features: {},
  status: 'loading',
  error: null,
  isReady: false,
  isFeatureEnabled: () => false,
});

export const FeatureFlagsProvider = ({ children }) => {
  const [features, setFeatures] = useState({});
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadFeatureFlags = async () => {
      try {
        const nextFeatures = await fetchFeatureFlags();
        if (!isMounted) {
          return;
        }

        setFeatures(nextFeatures);
        setStatus('ready');
        setError(null);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        console.warn('[FeatureFlags] Failed to load feature flags:', loadError);
        setFeatures({});
        setStatus('error');
        setError(loadError);
      }
    };

    loadFeatureFlags();

    return () => {
      isMounted = false;
    };
  }, []);

  const isFeatureEnabled = useCallback(
    (flagName) => checkFeatureEnabled(features, flagName),
    [features],
  );

  const value = useMemo(() => ({
    features,
    status,
    error,
    isReady: status !== 'loading',
    isFeatureEnabled,
  }), [features, status, error, isFeatureEnabled]);

  if (status === 'loading') {
    return <LoadingScreen text="Loading Tech Central" />;
  }

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagsContext);
